import { StateMachine } from '../../misc/StateMachine.js';
import { OEvent } from '../../misc/OEvent.js';
import { closeInstance } from '@refinio/one.core/lib/instance.js';
import RecipesStable from '../../recipes/recipes-stable.js';
import RecipesExperimental from '../../recipes/recipes-experimental.js';
import { SettingsStore } from '@refinio/one.core/lib/system/settings-store.js';
import { setBaseDirOrName } from '@refinio/one.core/lib/system/storage-base.js';
/**
 *
 * Base model class for future authentication workflows/scenarios. This class contains
 * the authentication state {@link authState} and the key-value store {@link store}, exposes events
 * to external sources, and implements some common functionality you might find in any workflow/scenario.
 */
export default class Authenticator {
    /**
     * This event will be triggered right AFTER the instance was initialised
     */
    onLogin = new OEvent();
    /**
     * This event will be triggered right BEFORE the instance was closed
     */
    onLogout = new OEvent();
    /**
     * JavaScript Immediately-invoked Function Expressions.
     * It returns the state machine with the registered states, events and transitions.
     * @type {StateMachine<AuthState, AuthEvent>}
     * @private
     */
    authState = (() => {
        const sm = new StateMachine();
        // Add the states
        sm.addState('logged_out');
        sm.addState('logging_in');
        sm.addState('logged_in');
        sm.addState('logging_out');
        // Add the events
        sm.addEvent('login');
        sm.addEvent('login_failure');
        sm.addEvent('login_success');
        sm.addEvent('logout');
        sm.addEvent('logout_done');
        // Add the transitions
        sm.addTransition('login', 'logged_out', 'logging_in');
        sm.addTransition('login_failure', 'logging_in', 'logged_out');
        sm.addTransition('login_success', 'logging_in', 'logged_in');
        sm.addTransition('logout', 'logged_in', 'logging_out');
        sm.addTransition('logout_done', 'logging_out', 'logged_out');
        sm.setInitialState('logged_out');
        return sm;
    })();
    /**
     * Class configuration
     */
    config;
    /**
     * SettingsStoreApi
     */
    store = SettingsStore;
    constructor(options) {
        setBaseDirOrName(options.directory);
        this.config = {
            directory: options.directory,
            recipes: options.recipes === undefined
                ? [...RecipesStable, ...RecipesExperimental]
                : options.recipes,
            reverseMaps: options.reverseMaps === undefined ? undefined : options.reverseMaps,
            reverseMapsForIdObjects: options.reverseMapsForIdObjects === undefined
                ? undefined
                : options.reverseMapsForIdObjects,
            storageInitTimeout: options.storageInitTimeout
        };
    }
    /**
     * Logouts the user. This function will:
     *  - trigger the 'logout' event
     *  - trigger onLogout and wait for all the listeners to finish
     *  - close the instance
     *  - trigger the 'logout_done' event if it is successfully
     */
    async logout() {
        this.authState.triggerEvent('logout');
        // Signal the application that it should shutdown one dependent models
        // and wait for them to shut down
        await this.onLogout.emitAll();
        closeInstance();
        this.authState.triggerEvent('logout_done');
    }
}
//# sourceMappingURL=Authenticator.js.map